import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useLocalStorage } from 'react-use'
import useGraphQLPolling from 'apps/marketplace/utils/hooks/useGraphQLPolling'
import { useUserDetails } from 'utils/hooks'
import { useNotificationsListQuery } from '../graphql/NotificationsList'

const POLL_INTERVAL_MS = 10000

interface IUseNotificationsPollingProps {
  /**
   * Whether to poll the backend
   * @default true
   */
  isPollingEnabled?: boolean
}

/**
 * Handles subscription to user notifications via GraphQL
 */
export const useNotificationsPolling = (props: IUseNotificationsPollingProps = {}) => {
  const { userDetails } = useUserDetails()
  const { isPollingEnabled = true } = props || {}
  const { pathname: currentUrl = '' } = useLocation()
  const [notificationsMuted, setNotificationsMuted] = useLocalStorage<string>(
    'notificationsMuted',
    '1',
    { raw: true }
  )
  const { data, error, loading, startPolling, stopPolling, ...rest } = useNotificationsListQuery({
    variables: { currentUrl },
    pollInterval: POLL_INTERVAL_MS,
    skip: !userDetails,
  })

  useGraphQLPolling({
    startPolling,
    stopPolling,
    pollInterval: POLL_INTERVAL_MS,
    isPollingEnabled,
  })

  const { notifications = [] } = data || {}

  // latest unreadCount based on the latest executed query
  const unreadCount = useMemo(() => {
    return notifications.filter(n => n.readAt === null).length
  }, [notifications])

  return {
    ...rest,
    data,
    error,
    loading,
    notifications,
    notificationsMuted,
    setNotificationsMuted,
    unreadCount,
  }
}
